import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FolderOffOutlinedIcon from "@mui/icons-material/FolderOffOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import { useTranslation } from "react-i18next";
import { pagePrimaryButtonSx } from "../../styles/pageModernStyles";
import {
  bob,
  cloudLift,
  motionSafe,
  rise,
  studioDialogBackdropSx,
  studioDialogPaperSx,
} from "../AI/studioDialogMotion";

const ACCENT_EMPTY = "#c62828";
const ACCENT_BLOCKED = "#ef6c00";

function DeleteStage({ blocked, accent }) {
  return (
    <Box
      aria-hidden
      sx={{
        position: "relative",
        height: 88,
        mt: 1.1,
        mb: 0.25,
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: alpha(accent, 0.08),
        border: "1px solid",
        borderColor: alpha(accent, 0.22),
        animation: `${rise} 320ms ease both`,
        ...motionSafe,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          left: "22%",
          top: 18,
          width: 48,
          height: 52,
          borderRadius: 1.5,
          bgcolor: "#fff",
          border: "1.5px solid",
          borderColor: alpha(accent, 0.4),
          display: "grid",
          placeItems: "center",
          boxShadow: `0 6px 14px ${alpha(accent, 0.14)}`,
          animation: `${bob} 2.2s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        {blocked ? (
          <Inventory2OutlinedIcon sx={{ fontSize: 26, color: accent }} />
        ) : (
          <FolderOffOutlinedIcon sx={{ fontSize: 26, color: accent }} />
        )}
      </Box>
      <Box
        sx={{
          position: "absolute",
          right: "20%",
          top: 14,
          width: 56,
          height: 56,
          borderRadius: 1.75,
          bgcolor: alpha(accent, 0.16),
          border: "1.5px solid",
          borderColor: alpha(accent, 0.5),
          display: "grid",
          placeItems: "center",
          boxShadow: `0 8px 18px ${alpha(accent, 0.18)}`,
          animation: `${cloudLift} 2.4s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <DeleteOutlineIcon sx={{ fontSize: 30, color: accent }} />
      </Box>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            left: `${38 + i * 8}%`,
            bottom: 14,
            width: 7,
            height: 7,
            borderRadius: "50%",
            bgcolor: alpha(accent, 0.3 + i * 0.15),
            animation: `${bob} ${1.8 + i * 0.2}s ease-in-out infinite`,
            animationDelay: `${i * 0.1}s`,
            ...motionSafe,
          }}
        />
      ))}
    </Box>
  );
}

/**
 * Confirmación Compás para borrar un proyecto vacío.
 * Si tiene contenido, avisa y no permite borrar.
 */
export default function SkillAgentDeleteProjectDialog({
  open,
  onClose,
  projectName = "",
  itemCount = 0,
  meetingCount = 0,
  artifactCount = 0,
  saving = false,
  error = "",
  onConfirm,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const blocked = Number(itemCount) > 0;
  const accent = blocked ? ACCENT_BLOCKED : ACCENT_EMPTY;
  const name = String(projectName || "").trim() || "—";

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="xs"
      keepMounted={false}
      transitionDuration={180}
      slotProps={{
        backdrop: { sx: studioDialogBackdropSx },
      }}
      BackdropProps={{ sx: studioDialogBackdropSx }}
      PaperProps={{
        sx: {
          ...studioDialogPaperSx,
          animation: open ? `${rise} 280ms ease both` : undefined,
          ...motionSafe,
        },
      }}
    >
      <Box
        sx={{
          px: 2.5,
          pt: 2,
          pb: 1.25,
          borderBottom: "1px solid",
          borderColor: "divider",
          background: `linear-gradient(135deg, ${alpha(accent, 0.14)} 0%, ${alpha(
            accent,
            0.02,
          )} 55%)`,
          animation: `${rise} 300ms ease both`,
          ...motionSafe,
        }}
      >
        <Typography variant="overline" sx={{ color: accent, fontWeight: 800 }}>
          {t("skillAgent.dialog.deleteProjectEyebrow", {
            defaultValue: "Compás · Proyecto",
          })}
        </Typography>
        <Typography variant="h6" fontWeight={800} lineHeight={1.25}>
          {blocked
            ? t("skillAgent.dialog.deleteProjectBlockedTitle", {
                defaultValue: "No se puede borrar aún",
              })
            : t("skillAgent.dialog.deleteProjectTitle", {
                defaultValue: "¿Eliminar proyecto?",
              })}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {blocked
            ? t("skillAgent.dialog.deleteProjectBlockedLead", {
                defaultValue:
                  "«{{name}}» tiene contenido. Borra primero reuniones, diseños, searches, moodboards o rapports.",
                name,
              })
            : t("skillAgent.dialog.deleteProjectLead", {
                defaultValue:
                  "Se quita «{{name}}» del árbol Compás. Esta acción no se puede deshacer.",
                name,
              })}
        </Typography>
        <DeleteStage blocked={blocked} accent={accent} />
      </Box>

      <DialogContent sx={{ px: 2.5, pt: 1.75, pb: 1 }}>
        <Stack spacing={1.25}>
          {blocked ? (
            <Box
              sx={{
                px: 1.5,
                py: 1.25,
                borderRadius: 2,
                bgcolor: isDark
                  ? alpha(accent, 0.12)
                  : alpha(accent, 0.08),
                border: "1px solid",
                borderColor: alpha(accent, 0.28),
              }}
            >
              <Typography
                variant="caption"
                fontWeight={800}
                sx={{ color: accent, letterSpacing: 0.04 }}
              >
                {t("skillAgent.dialog.deleteProjectInside", {
                  defaultValue: "Dentro ahora",
                })}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.35, fontWeight: 600 }}>
                {t("skillAgent.dialog.deleteProjectCounts", {
                  defaultValue:
                    "{{total}} elemento(s): {{meetings}} reunión(es), {{artifacts}} pieza(s).",
                  total: Number(itemCount) || 0,
                  meetings: Number(meetingCount) || 0,
                  artifacts: Number(artifactCount) || 0,
                })}
              </Typography>
            </Box>
          ) : null}
          {error ? (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {error}
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, pb: 2, pt: 0.5, gap: 1 }}>
        <Button onClick={onClose} disabled={saving} color="inherit">
          {blocked
            ? t("skillAgent.common.close", { defaultValue: "Cerrar" })
            : t("skillAgent.common.cancel")}
        </Button>
        {!blocked ? (
          <Button
            variant="contained"
            color="error"
            disabled={saving}
            onClick={onConfirm}
            startIcon={
              saving ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <DeleteOutlineIcon />
              )
            }
            sx={{
              ...pagePrimaryButtonSx,
              bgcolor: ACCENT_EMPTY,
              "&:hover": { bgcolor: "#b71c1c" },
            }}
          >
            {t("skillAgent.dialog.deleteProjectSubmit", {
              defaultValue: "Eliminar proyecto",
            })}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}
